import DataGrid, { Column, Scrolling } from "devextreme-react/cjs/data-grid";
import { useEffect, useMemo, useState, type FC } from "react";
import { Card, Col, Row, Button } from "react-bootstrap";
import AddCustomer from "../../transactions/estimate-payment-receipt/estimate-payment-receipt/components/CustomerModal";
import PaymentModal from "../../transactions/estimate-payment-receipt/estimate-payment-receipt/components/PaymentModal";
import SalesPersonModal from "../../transactions/estimate-payment-receipt/estimate-payment-receipt/components/SalesPersonModal";
import { PaymentEntry } from "../../transactions/estimate-payment-receipt/estimate-payment-receipt/models/payment-form-model";
import notistack from "modules/core/notistack";
import { useDispatch, useSelector } from "react-redux";
import { SetCunsumerId } from "slices/cunsumerOffer/reducer";
import { handleKeyDown } from "Common/modal/ModalTriggerButton";
import Loader from "Common/Loader";
import { RootState } from "slices/app/store/store";
import { clearProducts } from "slices/authSlice/interStoreProductSlice";
import { InsertInterStoreTransfer } from "../services/interStore-transfer.service";
import { useNavigate } from "react-router-dom";
// import { unstable_useBlocker as useBlocker } from "react-router-dom";

interface InterStorePaymentDetailsProps { }

const InterStorePaymentDetails: FC<InterStorePaymentDetailsProps> = () => {
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showSalesPersonModal, setShowSalesPersonModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDiscountFreeze, setIsDiscountFreeze] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [billingAddressId, setBillingAddressId] = useState<any>()

    const [customerData, setCustomerData] = useState<object | any>();
    const [salesPersonId, setSalesPersonId] = useState<string | any>();
    const [paymentData, setPaymentData] = useState<PaymentEntry[]>([]);
    const [editingPaymentData, setEditingPaymentData] = useState<PaymentEntry[]>([]);

    const dispatch = useDispatch();
    const product = useSelector((state: RootState) => state.interStoreProduct.products);

    const renderHeaderCell = (headerInfo: any) => {
        return <div className="text-nowrap">{headerInfo.column.caption}</div>;
    };

    const handleCustomerClickModal = () => setShowCustomerModal(true);
    const handleCustomerClick = (data: any) => setCustomerData(data);
    const handleSalesPersonClick = () => setShowSalesPersonModal(true);
    const handleSalesPersonSelect = (salesPId: string) => setSalesPersonId(salesPId);
    const closeModal = () => setShowCustomerModal(false);


    const openAddPaymentModal = () => {
        setEditingPaymentData([]);
        setShowPaymentModal(true);
    };

    const handlePaymentData = (data: PaymentEntry[]) => setPaymentData(data);

    const openEditPaymentModal = () => {
        setEditingPaymentData(paymentData);
        setShowPaymentModal(true);
    };

    const calculations = useMemo(() => {
        const totalPaidAmt = paymentData.reduce(
            (sum, payment) => sum + (Number(payment?.amount) || 0), 0
        );

        const gstAmount = product.length > 0 && product[0].isSrp
            ? product[0].gst
            : 0;
        const netAmt = product.reduce((sum, p) => sum + (p.customerAmount ?? 0), 0);
        const totalAmount = netAmt + gstAmount;
        const dueAmt = Math.max(0, totalAmount - totalPaidAmt);
        const excessPayment = Math.max(0, totalPaidAmt - totalAmount);

        return { totalPaidAmt, netAmt, gstAmount, totalAmount, dueAmt, excessPayment };
    }, [paymentData, product]);

    const navigate = useNavigate();

    const bid = localStorage.getItem('bid')

    const ProductPyament = async () => {
        try {
            if (!customerData?.id) return notistack.warning("Please select a customer");
            if (typeof bid !== 'string' || bid.trim() === "") return notistack.warning("Please select a customer address");
            if (!salesPersonId) return notistack.warning("Please select a sales person");
            if (calculations.totalPaidAmt <= 0) return notistack.warning("At least one payment method is required.");

            setIsSubmitting(true);
            const storeDataId = localStorage.getItem("storedataid");
            const payload = product.map((p) => ({
                requestedStoreId: parseInt(storeDataId!),
                salesPersonId: parseInt(salesPersonId),
                customerId: customerData.id,
                BillingAddressId: bid ?? 0,
                totalAmount: (p.isSrp ? p.gst + (p.customerAmount) : (p.customerAmount)),
                paidAmount: 0,
                dpPrice: (p.dpPrice ?? 0),
                dpPriceGst: p.isSrp ? 0.03 * (p.dpPrice ?? 0) : 0,
                gstAmount: p.isSrp ? p.gst : 0,
                productHeaderId: p.id,
                ktId: p.ktId,
                StyleNo: p.styleNumber,
                Size: p.size,
                advancePaymentDetails: paymentData.map((pay) => ({
                    paymentType: pay.type,
                    amount: Number(pay.amount),
                    refNo: pay.referenceNumber || ""
                }))
            }));

            const response = await InsertInterStoreTransfer(payload[0]);
            if (response.status !== 200 && response.status !== 201) {
                notistack.notifyFromApi(response);
                return
            }

            notistack.notifyFromApi(response);
            setIsFormSubmitted(true);
            handleCancel(true);
            localStorage.removeItem('bid')
        } catch (error: any) {
            notistack.error(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = (isSuccess = false) => {
        if (!isSuccess && (product.length > 0 || paymentData.length > 0 || customerData || salesPersonId)) {
            const confirmLeave = window.confirm(
                "Changes that you made may not be saved."
            );
            if (!confirmLeave) return;
        }

        setCustomerData(null);
        setPaymentData([]);
        setSalesPersonId(0);
        dispatch(SetCunsumerId(null));
        dispatch(clearProducts());
        localStorage.removeItem('bid')
        navigate("/interStore-transfer");
    };


    const productData = product;

    useEffect(() => {
        const isActive = sessionStorage.getItem("interStoreActive") === "true";
        // Detect reload only if page was already active
        const navigationType =
            performance.getEntriesByType("navigation")[0]?.type || "";
        if (navigationType === "reload" && isActive) {
            alert("You refreshed the page. Redirecting to main page.");
            localStorage.removeItem('bid')
            navigate("/interStore-transfer");
        }
        // Mark page as active for subsequent reloads
        sessionStorage.setItem("interStoreActive", "true");
        // Optional: clear on unmount
        return () => {
            sessionStorage.removeItem("interStoreActive");
        };
    }, [navigate]);
    // Store change detection
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "storedataid") {
                const isActive = sessionStorage.getItem("interStoreActive") === "true";
                if (isActive) {
                    alert("You refreshed the page. Redirecting to main page.");
                    localStorage.removeItem('bid')
                    navigate("/interStore-transfer");
                }
            }
        };
        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, [navigate]);


    useEffect(() => {
        const handlePopState = (e: PopStateEvent) => {
            e.preventDefault();
            const confirmLeave = window.confirm(
                "Changes that you made may not be saved. Do you really want to leave?"
            );

            if (confirmLeave) {
                setCustomerData(null);
                setPaymentData([]);
                setSalesPersonId(0);
                dispatch(SetCunsumerId(null));
                dispatch(clearProducts());
                navigate("/interStore-transfer");
            } else {
                // Push current state back so user stays
                window.history.pushState(null, "", window.location.href);
            }
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, [dispatch, navigate]);


    return (
        <div className="page-content">
            {isSubmitting && <Loader />}

            <div className="container-fluid">
                <Card className="border-0">
                    <Card.Header>
                        <h5>Inter Store Product - Payment Details</h5>
                    </Card.Header>

                    <Card.Body>
                        <Row>
                            <Col lg={9}>
                                <div className="m-2 d-flex gap-2 flex-wrap">
                                    <Button
                                        variant={`outline-primary ${customerData && 'bg-success text-white border-white'}`}
                                        size="sm"
                                        onKeyDown={handleKeyDown}
                                        onClick={handleCustomerClickModal}
                                    >
                                        <i className="bx bx-user me-1"></i> Customer
                                    </Button>

                                    <Button
                                        variant={`outline-primary ${salesPersonId && 'bg-success text-white border-white'}`}
                                        size="sm"
                                        onClick={handleSalesPersonClick}
                                    >
                                        <i className="bx bx-user-check me-1"></i> Sales Person
                                    </Button>

                                    <Button
                                        variant={`outline-primary ${paymentData.length > 0 && 'bg-success text-white border-white'}`}
                                        onKeyDown={handleKeyDown}
                                        size="sm"
                                        onClick={paymentData.length > 0 ? openEditPaymentModal : openAddPaymentModal}
                                    >
                                        <i className="bx bx-credit-card me-1"></i> Payment
                                    </Button>
                                </div>

                                <DataGrid
                                    dataSource={productData}
                                    keyExpr="id"
                                    showBorders={true}
                                    allowColumnReordering
                                    allowColumnResizing
                                    columnAutoWidth
                                    wordWrapEnabled
                                    noDataText="No product selected"
                                >
                                    <Scrolling
                                        mode='standard'
                                        useNative={true}
                                        scrollByContent={true}
                                        scrollByThumb={true}
                                    />
                                    <Column dataField="styleNumber" caption="Style Number" headerCellRender={renderHeaderCell} />
                                    <Column dataField="materialType" caption="Material Type" headerCellRender={renderHeaderCell} />
                                    <Column dataField="karatType" caption="Metal Kt" headerCellRender={renderHeaderCell} />
                                    <Column dataField="category" caption="Category" headerCellRender={renderHeaderCell} />
                                    <Column dataField="netWeight" caption="Net Weight" format="#,##0.000" headerCellRender={renderHeaderCell} />
                                    <Column dataField="size" caption="Size" headerCellRender={renderHeaderCell} />
                                    <Column dataField="dpPrice" caption="Dp Price" format="#,##0.00" headerCellRender={renderHeaderCell} />
                                    <Column dataField="customerAmount" caption="MRP" format="#,##0.00" />
                                    <Column dataField="huid" caption="HUID" headerCellRender={renderHeaderCell} />
                                    <Column dataField="stonePrice" caption="Stone Price" format="#,##0.00" headerCellRender={renderHeaderCell} />
                                    <Column dataField="isSrp" caption="Product Type"
                                        calculateCellValue={(rowData) => (rowData.isSrp ? "SRP" : "MRP")}
                                    />
                                </DataGrid>
                            </Col>

                            <Col lg={3}>
                                <div className="payment-calculator">
                                    <div className="summary-tables">
                                        <div className="mb-3">
                                            <table className="table table-bordered table-sm">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th colSpan={2} className="text-center">Summary</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>Product MRP</td>
                                                        <td className="text-end">₹ {calculations.netAmt.toFixed(2)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>GST (3% if SRP)</td>
                                                        <td className="text-end">₹ {Math.round((calculations.gstAmount + Number.EPSILON) * 100) / 100}</td>
                                                    </tr>
                                                    <tr className="table-info">
                                                        <td><strong>Total Amount</strong></td>
                                                        <td className="text-end"><strong>₹ {calculations.totalAmount.toFixed(2)}</strong></td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="mb-3">
                                            <table className="table table-bordered table-sm">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th colSpan={2} className="text-center">Payment Details</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {paymentData.map((payment, index) => (
                                                        <tr key={payment.id || index}>
                                                            <td style={{ color: "green" }} >{payment.type}</td>
                                                            <td style={{ color: "green" }} className="text-end">₹ {Number(payment.amount).toFixed(2)}</td>
                                                        </tr>
                                                    ))}
                                                    <tr className="table-info">
                                                        <td style={{ color: "green" }}><strong>Total Paid</strong></td>
                                                        <td style={{ color: "green" }} className="text-end"><strong>₹ {calculations.totalPaidAmt.toFixed(2)}</strong></td>
                                                    </tr>
                                                    <tr className={calculations.dueAmt > 0 ? "table-warning" : "table-success"}>
                                                        <td><strong>Due Amount</strong></td>
                                                        <td className="text-end"><strong>₹ {calculations.dueAmt.toFixed(2)}</strong></td>
                                                    </tr>
                                                    {calculations.excessPayment > 0 && (
                                                        <tr>
                                                            <td style={{ color: "green" }} ><strong>Advance Payment</strong></td>
                                                            <td style={{ color: "green" }} className="text-end"><strong>₹ {calculations.excessPayment.toFixed(2)}</strong></td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>

                    <Card.Footer>
                        <div className="d-flex gap-2 justify-content-end">
                            <button
                                className="btn submit-btn"
                                onClick={ProductPyament}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Processing...' : 'Submit Request'}
                            </button>

                            <button
                                className="btn add-btn"
                                onClick={() => handleCancel(false)}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </Card.Footer>
                </Card>
            </div>

            {/* Customer Modal */}
            <AddCustomer
                show={showCustomerModal}
                customerData={customerData}
                handleCustomerClick={handleCustomerClick}
                closeModal={closeModal}
                setBillingAddressId={setBillingAddressId}
            />

            {/* Payment Modal */}
            <PaymentModal
                show={showPaymentModal}
                onHide={() => setShowPaymentModal(false)}
                handelPaymentData={handlePaymentData}
            />

            {/* Sales Person Modal */}
            <SalesPersonModal
                show={showSalesPersonModal}
                onHide={() => setShowSalesPersonModal(false)}
                onSelect={handleSalesPersonSelect}
                selectedSalesPersonId={salesPersonId}
            />
        </div>
    );
};

export default InterStorePaymentDetails;